/**
 * ghost_text.ts — Copilot-style inline (ghost) completion for CodeMirror 6.
 *
 * The research-proven mechanics (codemirror-codeium / copilot-explorer /
 * Continue.dev): debounce after the last keystroke, suppress while unfocused or
 * while the autocomplete popup is open, render the suggestion as a
 * Decoration.widget after the cursor styled `.cm-ghostText`, accept with Tab at
 * highest keymap precedence, dismiss on any edit / Escape / blur, cache the
 * last suggestion by (prefix,suffix) so backspacing re-shows it for free, and
 * post-process the model output (strip fences, cut at the suffix echo, cap
 * lines) because chat models over-generate.
 */
import { StateEffect, StateField, Prec, RangeSet } from '@codemirror/state';
import { Decoration, EditorView, WidgetType, keymap, type DecorationSet } from '@codemirror/view';
import { completionStatus } from '@codemirror/autocomplete';

const setGhost = StateEffect.define<{ pos: number; text: string } | null>();

class GhostWidget extends WidgetType {
  constructor(readonly text: string) { super(); }
  override eq(other: GhostWidget): boolean { return other.text === this.text; }
  toDOM(): HTMLElement {
    const span = document.createElement('span');
    span.className = 'cm-ghostText';
    span.style.cssText = 'opacity:.45;font-style:italic;pointer-events:none;white-space:pre-wrap';
    span.textContent = this.text;
    return span;
  }
}

const ghostField = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(deco, tr) {
    for (const e of tr.effects) {
      if (e.is(setGhost)) {
        return e.value
          ? RangeSet.of([Decoration.widget({ widget: new GhostWidget(e.value.text), side: 1 }).range(e.value.pos)])
          : Decoration.none;
      }
    }
    // Any doc change / cursor move clears the ghost (a fresh request re-arms it).
    if (tr.docChanged || tr.selection) return Decoration.none;
    return deco;
  },
  provide: (f) => EditorView.decorations.from(f),
});

export interface GhostTextConfig {
  /** Fetch a completion for (prefix, suffix); return '' for none. Errors are swallowed. */
  fetch(prefix: string, suffix: string, signal: AbortSignal): Promise<string>;
  /** Live on/off gate (settings toggle + key presence) checked per keystroke. */
  enabled(): boolean;
  debounceMs?: number;
}

/** Post-process a raw model completion into a displayable insertion. */
export function cleanCompletion(raw: string, suffix: string): string {
  let text = raw.replace(/^```\w*\n?/, '').replace(/```\s*$/, '');
  // Cut where the model starts echoing the code after the cursor. A short
  // probe (12 chars) catches partial echoes a longer one would miss.
  const suffixHead = suffix.trimStart().slice(0, 12);
  if (suffixHead.length >= 6) {
    const echo = text.indexOf(suffixHead);
    if (echo >= 0) text = text.slice(0, echo);
  }
  // Cap at 3 lines — ghost text beyond that reads as noise (research: over-generation).
  const lines = text.split('\n');
  if (lines.length > 3) text = lines.slice(0, 3).join('\n');
  return text.replace(/\s+$/, '');
}

export function ghostText(cfg: GhostTextConfig): ReturnType<typeof Prec.highest>[] {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inflight: AbortController | null = null;
  let current: { pos: number; text: string } | null = null;
  const cache = new Map<string, string>(); // prefix-tail → completion (backspace re-show)

  const listener = EditorView.updateListener.of((update) => {
    if (!update.docChanged && !update.selectionSet) return;
    current = null;
    inflight?.abort();
    if (timer) clearTimeout(timer);
    if (!cfg.enabled() || !update.view.hasFocus) return;
    if (completionStatus(update.state) === 'active') return; // popup owns the keystroke
    const view = update.view;
    timer = setTimeout(() => {
      const state = view.state;
      const pos = state.selection.main.head;
      if (!state.selection.main.empty) return;
      const prefix = state.sliceDoc(Math.max(0, pos - 4000), pos);
      const suffix = state.sliceDoc(pos, Math.min(state.doc.length, pos + 1000));
      const key = prefix.slice(-200) + ' ' + suffix.slice(0, 60);
      const cached = cache.get(key);
      const show = (text: string): void => {
        if (!text || view.state.selection.main.head !== pos) return;
        current = { pos, text };
        view.dispatch({ effects: setGhost.of(current) });
      };
      if (cached !== undefined) { show(cached); return; }
      inflight = new AbortController();
      cfg.fetch(prefix, suffix, inflight.signal)
        .then((raw) => {
          const text = cleanCompletion(raw, suffix);
          cache.set(key, text);
          if (cache.size > 60) cache.delete(cache.keys().next().value as string);
          show(text);
        })
        .catch(() => { /* aborted / offline / bad key — ghost text just doesn't appear */ });
    }, cfg.debounceMs ?? 650);
  });

  const acceptKeymap = Prec.highest(keymap.of([
    {
      key: 'Tab',
      run: (view) => {
        if (!current) return false; // fall through to indent
        const { pos, text } = current;
        current = null;
        view.dispatch({
          changes: { from: pos, insert: text },
          selection: { anchor: pos + text.length },
          effects: setGhost.of(null),
        });
        return true;
      },
    },
    {
      key: 'Escape',
      run: (view) => {
        if (!current) return false;
        current = null;
        view.dispatch({ effects: setGhost.of(null) });
        return true;
      },
    },
  ]));

  return [ghostField as never, listener as never, acceptKeymap];
}
